import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useQuery } from 'react-query';
import { userService, brandService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', dealer: 'Dealer', sales_executive: 'Sales Executive' };

// Mirrors CREATABLE_ROLES on the backend (utils/userRoles.js)
const CREATABLE_ROLES = {
  super_admin: ['admin', 'dealer', 'sales_executive'],
  admin:       ['dealer', 'sales_executive'],
  dealer:      ['sales_executive'],
};

const COLUMNS = [
  { key: 'First Name', note: 'Required' },
  { key: 'Last Name',  note: 'Optional' },
  { key: 'Email',      note: 'Required — the login ID, must be unique' },
  { key: 'Phone',      note: 'Required — must be unique, no other user can have the same number' },
  { key: 'Role',       note: 'Required — Admin, Dealer or Sales Executive' },
  { key: 'Reports To', note: 'User ID (e.g. DLR-0007) or email of the person this user reports to — see the Reference sheet. Can also be the email of a user added higher up in this same sheet. A sales executive reports to a dealer, a dealer to an admin, an admin to the super admin. Leave blank to report to yourself, when your role fits.' },
  { key: 'Brands',     note: 'Dealers only, required — comma separated, e.g. Kia, Hyundai' },
  { key: 'Password',   note: 'Optional — leave blank to auto-generate. Min 6 characters with a lowercase letter, an uppercase letter and a number' },
  { key: 'Status',     note: 'Optional — active (default) or inactive' },
];

const blankRow = Object.fromEntries(COLUMNS.map(c => [c.key, '']));

// Example rows that work for the uploader's own role
function sampleRows(role, brandNames) {
  const brands = brandNames.slice(0, 2).join(', ') || 'Kia';
  const admin  = { ...blankRow, 'First Name': 'Amit',  'Last Name': 'Verma',  Email: 'amit.verma@example.com',  Phone: '9876500001', Role: 'Admin' };
  const dealer = { ...blankRow, 'First Name': 'Sunil', 'Last Name': 'Motors', Email: 'sunil.motors@example.com', Phone: '9876500002', Role: 'Dealer', Brands: brands };
  const se     = { ...blankRow, 'First Name': 'Ravi',  'Last Name': 'Kumar',  Email: 'ravi.kumar@example.com',  Phone: '9876500003', Role: 'Sales Executive' };
  if (role === 'super_admin') return [admin, { ...dealer, 'Reports To': admin.Email }, { ...se, 'Reports To': dealer.Email }];
  if (role === 'admin')       return [dealer, { ...se, 'Reports To': dealer.Email }];
  return [se];
}

function downloadTemplate(role, teamUsers, brandNames) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sampleRows(role, brandNames), { header: COLUMNS.map(c => c.key) }), 'Users');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(COLUMNS.map(c => ({ Column: c.key, 'How to fill': c.note }))), 'Instructions');

  // Who new users can report to: active admins and dealers in the uploader's team
  const parents = teamUsers
    .filter(u => ['super_admin', 'admin', 'dealer'].includes(u.role) && u.status === 'active')
    .map(u => ({ 'User ID': u.userCode || '', Name: `${u.firstName} ${u.lastName}`, Role: ROLE_LABELS[u.role] || u.role, Email: u.email }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(parents.length ? parents : [{ 'User ID': '', Name: '', Role: '', Email: '' }]), 'Reference');

  if ((CREATABLE_ROLES[role] || []).includes('dealer')) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(brandNames.map(name => ({ Brand: name }))), 'Brands');
  }
  XLSX.writeFile(wb, 'bulk_users_template.xlsx');
}

// Columns this download adds; dropped if the uploaded sheet already has them
// (an error file fixed and uploaded again). The import ignores both.
const ADDED_COLUMNS = ['sheet row', 'error'];

/**
 * The rows that failed, exactly as they were in the uploaded sheet — same
 * columns, same order — with "Sheet Row" and "Error" added at the end. Fix
 * the rows and upload this file again as it is.
 */
async function downloadErrors(file, errors) {
  try {
    const wb  = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const ws  = wb.Sheets[wb.SheetNames[0]];
    // One array per sheet line, blank lines included, each tagged with its
    // own position — the same numbering the server reports errors by.
    const lines  = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false, blankrows: true });
    const byRow  = new Map(lines.map((line, i) => [(Number.isInteger(line.__rowNum__) ? line.__rowNum__ : i) + 1, line]));
    const header = lines[0] || [];
    const keep   = header.map((_, i) => i).filter(i => !ADDED_COLUMNS.includes(String(header[i]).trim().toLowerCase()));

    const out = [
      [...keep.map(i => header[i]), 'Sheet Row', 'Error'],
      ...errors.map(e => {
        const line = byRow.get(e.row) || [];
        return [...keep.map(i => line[i] ?? ''), e.row, e.errors.join('; ')];
      }),
    ];
    const sheet = XLSX.utils.aoa_to_sheet(out);
    sheet['!cols'] = out[0].map((h, i) => ({ wch: i === out[0].length - 1 ? 60 : Math.max(String(h).length + 2, 14) }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Users');
    XLSX.writeFile(book, `${file.name.replace(/\.(csv|xlsx|xls)$/i, '')}-errors.xlsx`);
  } catch {
    // Couldn't re-read the upload — fall back to a plain list of the errors
    const rows = errors.map(e => ({ 'Sheet Row': e.row, Name: e.name, Error: e.errors.join('; ') }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(rows), 'Errors');
    XLSX.writeFile(book, 'bulk_users_errors.xlsx');
  }
}

function downloadCredentials(users) {
  const rows = users.map(u => ({
    'User ID': u.userCode, Name: u.name, Email: u.email, Password: u.password,
    Role: ROLE_LABELS[u.role] || u.role, 'Reports To': u.reportsTo, Status: u.status,
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Credentials');
  XLSX.writeFile(wb, 'new_users_credentials.xlsx');
}

const Stat = ({ value, label, tone }) => {
  const tones = {
    gray:  'bg-gray-50 text-gray-800',
    green: 'bg-green-50 text-green-600',
    red:   'bg-red-50 text-red-600',
  };
  return (
    <div className={`rounded-lg p-3 text-center ${tones[tone]}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs mt-0.5">{label}</p>
    </div>
  );
};

const RowMessages = ({ title, items, tone, render }) => (
  <div className={`rounded-lg border p-3 max-h-48 overflow-y-auto ${tone === 'red' ? 'border-red-200 bg-red-50 text-red-600' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
    <p className="text-xs font-semibold mb-2">{title}</p>
    {items.map((item, i) => (
      <div key={i} className="text-xs mb-1">
        <span className="font-medium">Row {item.row} ({item.name}):</span> {render(item)}
      </div>
    ))}
  </div>
);

export default function BulkUserUploadModal({ onClose, onSuccess }) {
  const { user } = useAuth();
  const role = user?.role === 'company' ? 'super_admin' : user?.role;
  const canCreateDealers = (CREATABLE_ROLES[role] || []).includes('dealer');

  const [file, setFile]         = useState(null);
  const [check, setCheck]       = useState(null);   // dry-run result
  const [result, setResult]     = useState(null);   // final result
  const [busy, setBusy]         = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  const { data: teamData } = useQuery('hierarchyUsers', () => userService.getHierarchy(), { staleTime: 5 * 60 * 1000 });
  const teamUsers = teamData?.data?.data?.users || teamData?.data?.users || [];

  const { data: brandsData } = useQuery('brandsForUserForm', () => brandService.getBrands(), {
    staleTime: 10 * 60 * 1000,
    enabled: canCreateDealers,
  });
  const brandNames = (brandsData?.data?.data?.brands || brandsData?.data?.brands || [])
    .filter(b => b.isActive !== false)
    .map(b => b.name);

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Only CSV and Excel (.xlsx/.xls) files are allowed');
      return;
    }
    setFile(f);
    setCheck(null);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const runCheck = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await userService.bulkUpload(file, { dryRun: true });
      setCheck(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not check the file');
    } finally {
      setBusy(false);
    }
  };

  const runCreate = async () => {
    setBusy(true);
    try {
      const res = await userService.bulkUpload(file);
      const data = res.data.data;
      setResult(data);
      if (data.limitError) toast.error(data.limitError);
      if (data.success > 0) {
        toast.success(`${data.success} user${data.success === 1 ? '' : 's'} created`);
        onSuccess && onSuccess();
      }
      if (data.failed > 0) toast.error(`${data.failed} row${data.failed === 1 ? '' : 's'} not created`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setCheck(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const shown = result || check;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black bg-opacity-40" onClick={busy ? undefined : onClose} />

        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <UserPlusIcon className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Users</h2>
            </div>
            <button onClick={onClose} disabled={busy} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            {!result && (
              <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50 rounded-lg px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-blue-800">Download Template</p>
                  <p className="text-xs text-blue-600 mt-0.5">Includes instructions and the User IDs your new users can report to</p>
                </div>
                <button onClick={() => downloadTemplate(role, teamUsers, brandNames)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 border border-blue-300 rounded-lg px-3 py-1.5 hover:bg-blue-100">
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  Template.xlsx
                </button>
              </div>
            )}

            {/* Drop zone */}
            {!check && !result && (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                  ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'}`}
              >
                <ArrowUpTrayIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                {file ? (
                  <p className="text-sm font-medium text-gray-700">{file.name}
                    <span className="ml-2 text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                  </p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-700">Drop your file here or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">CSV, XLSX, XLS — max 5 MB, up to 500 users</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])} />
              </div>
            )}

            {/* Check / result summary */}
            {shown && (
              <div className="space-y-3">
                {check && !result && (
                  <p className="text-sm text-gray-600">
                    Checked <span className="font-medium">{file?.name}</span> — nothing has been created yet.
                  </p>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <Stat value={shown.total} label="Rows" tone="gray" />
                  {result
                    ? <Stat value={result.success} label="Created" tone="green" />
                    : <Stat value={check.valid} label="Ready to create" tone="green" />}
                  <Stat value={shown.failed} label={result ? 'Not created' : 'With errors'} tone={shown.failed > 0 ? 'red' : 'gray'} />
                </div>

                {shown.limitError && (
                  <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                    <p>{shown.limitError}</p>
                  </div>
                )}

                {shown.errors.length > 0 && (
                  <>
                    <RowMessages title={result ? 'Not created:' : 'Rows with errors (will be skipped):'} items={shown.errors} tone="red"
                      render={(e) => e.errors.join(', ')} />
                    <button onClick={() => downloadErrors(file, shown.errors)}
                      title="The failed rows of your sheet with an Error column — fix them and upload this file again"
                      className="flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:underline">
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />Download failed rows (with errors)
                    </button>
                  </>
                )}

                {shown.warnings?.length > 0 && (
                  <RowMessages title="Warnings:" items={shown.warnings} tone="amber" render={(w) => w.message} />
                )}

                {result?.success > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-green-50 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                      <p className="text-sm">{result.success} user{result.success === 1 ? '' : 's'} created. Download their login details to share with them.</p>
                    </div>
                    <button onClick={() => downloadCredentials(result.users)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-green-700 border border-green-300 rounded-lg px-3 py-1.5 hover:bg-green-100">
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />Credentials.xlsx
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            {result ? (
              <button onClick={onClose} className="btn-primary text-sm px-6">Done</button>
            ) : check ? (
              <>
                <button onClick={reset} disabled={busy} className="btn-outline-secondary text-sm px-5">Choose another file</button>
                <button onClick={runCreate} disabled={busy || check.valid === 0 || !!check.limitError}
                  className="btn-primary text-sm px-6 flex items-center gap-2">
                  {busy
                    ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Creating...</>
                    : <><UserPlusIcon className="h-4 w-4" />Create {check.valid} user{check.valid === 1 ? '' : 's'}</>}
                </button>
              </>
            ) : (
              <>
                <button onClick={onClose} className="btn-outline-secondary text-sm px-5">Cancel</button>
                <button onClick={runCheck} disabled={!file || busy} className="btn-primary text-sm px-6 flex items-center gap-2">
                  {busy
                    ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Checking...</>
                    : <><ArrowUpTrayIcon className="h-4 w-4" />Check file</>}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
